import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { FormControl, Input, InputLabel, Tooltip } from '@mui/material'
import { type Field, PuiSelect } from '@pbt/pbt-ui-components'

import type { ReminderProtocol } from '~/api/graphql/generated/types'
import { LandingType } from '~/constants/landingConstants'
import SnapshotsAliasTypes from '~/constants/SnapshotsAliasTypes'
import { getWidgetData } from '~/store/duck/landing'
import { getMultipleReminders } from '~/store/duck/reminders'
import { getPatientName } from '~/store/reducers/patients'

export function checkDuplicateReminders(
  existingReminders?: { protocolId?: string }[] | null,
  reminderProtocols?: { id: string }[] | null,
): boolean {
  if (!Array.isArray(existingReminders) || !Array.isArray(reminderProtocols)) {
    return false
  }
  if (existingReminders.length === 0 || reminderProtocols.length === 0) {
    return false
  }
  const protocolIds = new Set(reminderProtocols.map((p) => p.id))
  return existingReminders.some(
    (r) => r.protocolId && protocolIds.has(r.protocolId),
  )
}

export function VaccineHistoryReminderSelect({
  reminder,
  reminderProtocols,
  reminderProtocolsIsLoading,
  defaultReminderProtocolId,
  patientId,
}: {
  defaultReminderProtocolId?: string
  patientId: string | null
  reminder: Field
  reminderProtocols: ReminderProtocol[]
  reminderProtocolsIsLoading: boolean
}) {
  const { t } = useTranslation(['MedicalHistory'])

  useEffect(() => {
    reminder.setValue(defaultReminderProtocolId)
  }, [defaultReminderProtocolId])

  const remindersIds = useSelector(
    getWidgetData(
      LandingType.CLIENT_AND_PATIENT_SNAPSHOTS,
      SnapshotsAliasTypes.Reminders,
    ),
  )

  const patientName = useSelector(getPatientName(patientId))
  const existingReminders = useSelector(getMultipleReminders(remindersIds))

  const hasDuplicateReminders = checkDuplicateReminders(
    existingReminders,
    reminderProtocols,
  )

  const SelectComponent = (
    <PuiSelect
      disabled={
        reminderProtocolsIsLoading ||
        reminderProtocols.length === 0 ||
        hasDuplicateReminders
      }
      field={reminder}
      input={<Input id="reminder-select" />}
      isLoading={reminderProtocolsIsLoading}
      items={
        reminderProtocolsIsLoading
          ? []
          : reminderProtocols.map((protocol) => ({
              name: protocol.reminderName,
              id: protocol.id,
            }))
      }
      label={reminder.label}
    />
  )

  const shouldShowTooltip =
    (!reminderProtocolsIsLoading && reminderProtocols.length === 0) ||
    hasDuplicateReminders

  return (
    <FormControl fullWidth>
      <InputLabel htmlFor="reminder-select">{reminder.label}</InputLabel>
      {shouldShowTooltip ? (
        <Tooltip
          placement="top"
          title={
            hasDuplicateReminders
              ? t(
                  'MedicalHistory:VACCINE_HISTORY_EXPANDED_DETAILS.LABEL.PATIENT_HAS_ACTIVE_REMINDER_VACCINE',
                  { patientName },
                )
              : t(
                  'MedicalHistory:VACCINE_HISTORY_EXPANDED_DETAILS.LABEL.NO_REMINDER_NEEDED',
                )
          }
        >
          {SelectComponent}
        </Tooltip>
      ) : (
        SelectComponent
      )}
    </FormControl>
  )
}
